<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { faDownload, faLink, faSpinner } from '@fortawesome/duotone-regular-svg-icons'
import { setTitle } from '~/utils/dom'
import useAuthStore from '~/stores/auth'
import useFileUpload from '~/composables/useFileUpload'
import useFileInfo from '~/composables/useFileInfo'
import { useFavorites } from '~/composables/useFavorites'
import { useFavoritesApi } from '~/api/favoritesApi'
import TopBar from '~/components/utils/TopBar.vue'
import Button from '~/components/ui/Button.vue'

const { itemId } = defineProps<{
  itemId: string
}>()

const { t } = useI18n()
const { safeName } = useFavorites()
const { getFavorite } = useFavoritesApi()
const { readableMimeType, readableFileSize, getFileIcon } = useFileInfo()
const { downloadFileKey, isDownloading } = useFileUpload()
const { isAuthenticated } = storeToRefs(useAuthStore())

const { data: favorite } = useQuery({
  queryKey: computed(() => ['favorite', itemId]),
  queryFn: () => getFavorite(itemId),
  staleTime: Infinity,
  enabled: computed(() => isAuthenticated.value && itemId.length > 0)
})

const name = computed(() => safeName(
  favorite?.value?.location?.title
  || favorite?.value?.file?.name
  || favorite?.value?.link?.name
  || favorite?.value?.name
  || ''
))

const mimeType = computed(() => favorite?.value?.file?.mimeType
  ? readableMimeType(favorite.value.file.mimeType || '')
  : '')

const fileSize = computed(() => favorite?.value?.file?.fileSize
  ? readableFileSize(favorite.value.file.fileSize || 0)
  : '')

const fileInfo = computed(() => [mimeType.value, fileSize.value].filter(Boolean).join(', '))

const downloadIcon = computed(() => {
  if (isDownloading.value) {
    return faSpinner
  }
  const mime = favorite?.value?.file?.mimeType
  return (mime && getFileIcon(mime)) || faDownload
})

const host = computed(() => {
  if (!favorite?.value?.link?.url)
    return ''
  try {
    return new URL(favorite.value.link.url).hostname
  } catch (e) {
    console.error(`Invalid URL: ${favorite.value.link.url} - ${e}`)
    return ''
  }
})

watch(name, () => name.value && setTitle(name.value), { immediate: true })
</script>

<template>
  <div class="flex h-screen">
    <TopBar :fullscreen="false" />
    <div class="m-auto">
      <template v-if="favorite?.file?.key">
        <Button
          size="xl"
          variant="solid"
          :href="favorite.file.key"
          :disabled="isDownloading"
          @click.prevent="downloadFileKey(favorite.file.key)"
        >
          <font-awesome-icon :icon="downloadIcon" />
          {{ t('downloadIn', { in: `"${name}"` }) }}
        </Button>
        <p
          v-if="fileInfo"
          class="text-gray-40 mt-2 text-center text-sm"
          v-text="fileInfo"
        />
      </template>
      <template v-if="favorite?.link?.url">
        <Button
          size="xl"
          variant="solid"
          :href="favorite.link.url"
          icon-left="link"
          target="_blank"
        >
          <font-awesome-icon :icon="faLink" />
          {{ t('visitIn', { in: `«${name}»` }) }}
        </Button>
        <p
          v-if="host"
          class="text-gray-40 mt-2 text-center text-sm"
          v-text="host"
        />
      </template>
    </div>
  </div>
</template>
